<script setup>
import { ref, computed, watch } from "vue";
import SlideMenuWrapper from "@/v2/components/SlideMenu/shared/SlideMenuWrapper.vue";
import SlideMenuOverlay from "@/v2/components/SlideMenu/shared/SlideMenuOverlay.vue";

const props = defineProps({
  isLeft: {
    type: Boolean,
    default: true,
  },
  isOverlay: {
    type: Boolean,
    default: false,
  },
  hasFade: {
    type: Boolean,
    default: true,
  },
  isSideMenu: {
    type: Boolean,
    default: true,
  },
  useSurfaceColors: {
    type: Boolean,
    default: true,
  },
  shouldTeleport: {
    type: Boolean,
    default: false,
  },
  openOnInit: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["open"]);

const isOpen = ref(props.openOnInit);
const teleportTo = ref("body");

const componentClasses = computed(() => ({
  "slide-menu": true,
  "slide-menu--open": isOpen.value,
  "slide-menu--overlay": props.isOverlay,
  "slide-menu--left": props.isLeft,
  "slide-menu--is-search": !props.isSideMenu,
  "slide-menu--is-side-menu": props.isSideMenu,
}));

const close = () => {
  isOpen.value = false;
  emit("open", isOpen.value);
};

const open = () => {
  isOpen.value = true;
  emit("open", isOpen.value);
};

watch(
  () => isOpen.value,
  () => {
    teleportTo.value = document.getElementById("app-shrinker-modal-target")
      ? "#app-shrinker-modal-target"
      : "body";
  },
  {
    immediate: true,
  }
);

defineExpose({
  open,
  close,
  isOpen,
});
</script>

<template>
  <Teleport :to="teleportTo" :disabled="!shouldTeleport">
    <div :class="componentClasses">
      <SlideMenuWrapper
        :is-open
        :is-overlay
        :is-left
        :use-surface-colors
        :has-fade
        @close="close"
      >
        <slot v-if="isOpen" name="default" />
      </SlideMenuWrapper>
    </div>

    <SlideMenuOverlay v-if="isOverlay && isOpen" @click="close" />
  </Teleport>
</template>

<style scoped lang="scss">
@import "@/v2/styles/main";

.slide-menu {
  z-index: var(--z-index-slide-menu);
  position: absolute;
  top: 0;
  right: 0;
  left: auto;
  height: 100%;
  pointer-events: none;
  width: 100%;

  &--left {
    left: 0;
    right: auto;
  }

  &--open {
    overflow: visible;
    pointer-events: all;
  }

  &--is-search {
    z-index: var(--z-index-slide-menu-search);
    min-width: grid-column(3, 7);

    @include min-width-md-and-height-md {
      width: grid-column(7, 8);
    }

    @include min-width-md {
      width: grid-column(11, 11);
    }
  }

  &--is-side-menu {
    min-width: grid-column(3, 7);
  }
}
</style>
